/* eslint-disable camelcase */

import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material'
import { ReactElement, useEffect, useState } from 'react'
import axios from 'axios'
import jwt_decode from 'jwt-decode'
import { useAuth0 } from '@auth0/auth0-react'
import { LaunchIcon } from 'src/v3/core/icons'

// ENG-859: where a user whose renewal failed (`past-due` claim) is sent instead of /subscribe. Paying the
// open invoice on Stripe's hosted page fires invoice.paid, and Studio gives access back.

type Invoice =
  | { status: 'pending' }
  | { status: 'found'; invoiceUrl: string }
  | { status: 'none' }
  | { status: 'error' }

let openInvoiceUrl = `${import.meta.env.VITE_PUBLIC_GATEWAY_URL}/api/billing/open-invoice`

let supportEmail = 'support@foamfrat.com'

export default (): ReactElement => {
  let {
    getAccessTokenSilently,
    logout,
    isAuthenticated,
    isLoading,
    loginWithRedirect,
  } = useAuth0()
  let [invoice, setInvoice] = useState<Invoice>({ status: 'pending' })
  let [checking, setChecking] = useState(false)
  let [stillPastDue, setStillPastDue] = useState(false)

  // UnauthenticatedLayout doesn't require a session, so sign in here and come back (the same return-path
  // pattern as PrivateRoute, which can't be used: it requires a subscribed session).
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      sessionStorage.setItem('auth_return_to', '/payment-failed')
      loginWithRedirect({ appState: { returnTo: '/payment-failed' } })
    }
  }, [isAuthenticated, isLoading, loginWithRedirect])

  useEffect(() => {
    if (isLoading || !isAuthenticated) {
      return
    }

    getAccessTokenSilently()
      .then((token) =>
        axios.get<{ invoiceUrl: string }>(openInvoiceUrl, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      )
      .then(({ data }) =>
        setInvoice({ status: 'found', invoiceUrl: data.invoiceUrl }),
      )
      .catch((error) =>
        setInvoice(
          error?.response?.status === 404
            ? { status: 'none' }
            : { status: 'error' },
        ),
      )
  }, [getAccessTokenSilently, isAuthenticated, isLoading])

  // A fresh token re-runs the login claims, so it says `subscribed` once Studio has the payment.
  let checkPayment = () => {
    if (checking) {
      return
    }
    setChecking(true)
    setStillPastDue(false)
    getAccessTokenSilently({ cacheMode: 'off' })
      .then((token) => {
        let decoded: Record<string, string> = jwt_decode(token)
        if (decoded['studio-subscription-status'] === 'subscribed') {
          // eslint-disable-next-line fp/no-mutation
          window.location.href = '/self-paced'
        } else {
          setStillPastDue(true)
        }
      })
      .catch(() => setStillPastDue(true))
      .finally(() => setChecking(false))
  }

  return (
    <Box component="main" sx={{ backgroundColor: 'black' }}>
      <Box className="flex items-center justify-center mt-2">
        <Box sx={{ width: 200 }}>
          <img
            style={{ width: '100%', height: 'auto' }}
            src="/logo.svg"
            alt="FOAMfrat Studio logo"
          />
        </Box>
      </Box>
      <Box className="flex flex-col items-center text-center px-4">
        <Typography component="h1" variant="h5" className="my-2">
          Your subscription payment didn&apos;t go through
        </Typography>
        <Typography className="mb-4">
          Your access is paused until it&apos;s paid. Pay your invoice to pick
          up where you left off. You can use a different card.
        </Typography>

        {invoice.status === 'pending' && (
          <CircularProgress aria-label="Finding your invoice" />
        )}

        {invoice.status === 'found' && (
          <Button
            variant="contained"
            startIcon={<LaunchIcon />}
            href={invoice.invoiceUrl}
          >
            Pay invoice
          </Button>
        )}

        {(invoice.status === 'none' || invoice.status === 'error') && (
          <Alert severity="info" className="mb-2">
            We couldn&apos;t find an invoice to pay. Email{' '}
            <a href={`mailto:${supportEmail}`}>{supportEmail}</a> and we&apos;ll
            sort it out.
          </Alert>
        )}

        <Box className="flex gap-2 mt-4">
          {/* aria-disabled, not disabled: a disabled button drops keyboard focus to the page. */}
          <Button
            variant="outlined"
            onClick={checkPayment}
            aria-disabled={checking}
          >
            I&apos;ve paid
          </Button>
          <Button
            variant="text"
            onClick={() =>
              logout({
                logoutParams: {
                  returnTo: import.meta.env.VITE_AUTH0_LOGOUT_REDIRECT_URI,
                },
              })
            }
          >
            Log out
          </Button>
        </Box>

        {/* Always rendered, so screen readers announce the message when it appears. */}
        <Box role="status" aria-live="polite" className="mt-2">
          {stillPastDue && (
            <Alert severity="warning" role="none">
              We haven&apos;t received the payment yet. It can take a minute
              after you pay. Try again shortly.
            </Alert>
          )}
        </Box>
      </Box>
    </Box>
  )
}
